import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import useAuth from "../../hooks/useAuth";
import { DayPlan, instantDayPlanningApi } from "../../api/instantDayPlanning";

export default function InstantDayPlanDialog({ open, date, kitchenId, kitchenName, onClose, onUpdated }: {
  open: boolean; date: string; kitchenId: string; kitchenName: string; onClose: () => void; onUpdated: () => Promise<void>;
}) {
  const { admin } = useAuth();
  const canCreate = admin && (admin.permissions?.nowMeals ?? admin.role !== "kitchen-staff");
  const [plan, setPlan] = useState<DayPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tab, setTab] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [mealId, setMealId] = useState("");
  const [stockMode, setStockMode] = useState("LIMITED");
  const [quantity, setQuantity] = useState("0");
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setPlan(null); setError(""); setNotice(""); setEditorOpen(false); setTab(0);
    instantDayPlanningApi.getPlan(date, kitchenId).then(data => {
      if (!cancelled) setPlan(data);
    }).catch(() => { if (!cancelled) setError("Could not load day plan"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, date, kitchenId]);
  const editRule = (id = "") => { setMealId(id); setStockMode("LIMITED"); setQuantity("0"); setEditorOpen(true); setNotice(""); };
  const saveRule = async () => {
    if (!mealId || (stockMode === "LIMITED" && (quantity === "" || !Number.isInteger(Number(quantity)) || Number(quantity) < 0))) {
      setError("Choose a meal and a non-negative whole quantity"); return;
    }
    setSaving(true); setError(""); setNotice("");
    try {
      const result = await instantDayPlanningApi.saveRule({ date, kitchenId, mealId, stockMode, quantity: Number(quantity) });
      setEditorOpen(false);
      setNotice(result.generation?.success === false
        ? "Rule saved. Recalculation incomplete; recalculate availability to retry."
        : `Date rule saved. ${result.generation?.overridesPreserved || 0} daily overrides kept.`);
      setPlan(await instantDayPlanningApi.getPlan(date, kitchenId));
      await onUpdated();
    } catch (err: any) { setError(err.response?.data?.message || "Could not save or refresh the date rule"); }
    finally { setSaving(false); }
  };
  return <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="lg" fullWidth
    sx={{ "& .MuiDialog-paper": { m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" }, maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" } } }}>
    <DialogTitle>Day Plan: {date}<Typography variant="body2" color="text.secondary">{kitchenName}</Typography></DialogTitle>
    <DialogContent dividers sx={{ minHeight: 300, minWidth: 0, p: { xs: 1.5, sm: 3 } }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}
      {loading ? <CircularProgress aria-label="Loading day plan" /> : plan && <>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
            <Typography fontWeight={600}>Swap Set {plan.swapSetSerial}{!plan.swapSetActive ? " (Inactive / Missing)" : ""}</Typography>
            <Typography>{plan.orderCount} assigned subscription orders</Typography>
            <Typography>{plan.scheduledMeals.length} scheduled Instant meals{plan.isProjected ? " (Projected)" : ""}</Typography>
          </Stack>
          {canCreate && <Button startIcon={<AddIcon />} onClick={() => editRule()} disabled={saving}>Date Rule</Button>}
        </Stack>
        {editorOpen && <Box component="form" onSubmit={event => { event.preventDefault(); void saveRule(); }} sx={{ py: 2, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} sx={{ flexWrap: "wrap", gap: 2 }} alignItems={{ xs: "stretch", sm: "center" }}>
            <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 220 }, flex: 1 }}>
              <InputLabel id="plan-meal-label">Instant Meal</InputLabel>
              <Select labelId="plan-meal-label" label="Instant Meal" value={mealId} onChange={event => setMealId(event.target.value)} disabled={saving}>
                {plan.catalogue.map(meal => <MenuItem key={meal.id} value={meal.id}>{meal.name}</MenuItem>)}
              </Select>
            </FormControl>
            <ToggleButtonGroup value={stockMode} exclusive onChange={(_, value) => value && setStockMode(value)} size="small" disabled={saving}>
              <ToggleButton value="LIMITED">Limited</ToggleButton><ToggleButton value="UNLIMITED">Unlimited</ToggleButton>
            </ToggleButtonGroup>
            {stockMode === "LIMITED" && <TextField size="small" label="Daily Capacity" type="number" inputProps={{ min: 0, step: 1 }} value={quantity} onChange={event => setQuantity(event.target.value)} disabled={saving} sx={{ width: { xs: "100%", sm: 140 } }} />}
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={saving}>Save for {date}</Button>
            <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancel</Button>
          </Stack>
        </Box>}
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2,
          "& .MuiTab-root": { maxWidth: { xs: 205, sm: 360 }, whiteSpace: "normal" } }}><Tab label="Subscription Menu & Demand" /><Tab label="Scheduled Instant Meals" /></Tabs>
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead><TableRow><TableCell>Meal / Drink</TableCell><TableCell>{tab === 0 ? "Source" : "Stock"}</TableCell><TableCell>{tab === 0 ? "Subscription Portions" : "Status"}</TableCell><TableCell>{tab === 0 ? "Instant Schedule" : "Actions"}</TableCell></TableRow></TableHead>
            <TableBody>
              {tab === 0 ? plan.rows.map(row => <TableRow key={`${row.kind}:${row.id}`}>
                <TableCell>{row.name}</TableCell><TableCell>{row.origins.join(", ")}</TableCell><TableCell>{row.demand}</TableCell>
                <TableCell>{row.instantMeals.length ? row.instantMeals.map(meal => <Stack key={meal.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.5 }}>
                  <Typography variant="body2">{meal.name}: {meal.scheduled ? meal.isAvailable ? "In Stock" : "Out of Stock" : "Not Scheduled"}{meal.sharedStockGroupId ? " (Shared Stock)" : ""}</Typography>
                  {canCreate && <Button size="small" startIcon={<AddIcon />} disabled={saving} onClick={() => editRule(meal.id)}>Date Rule</Button>}
                </Stack>) : <Typography variant="body2" color="error.main">No active Instant meal linked</Typography>}</TableCell>
              </TableRow>) : plan.scheduledMeals.map(record => <TableRow key={record._id}>
                <TableCell>{record.mealId.name}</TableCell><TableCell>{record.stockMode === "UNLIMITED" ? "Unlimited" : record.remainingQuantity || 0}</TableCell><TableCell>{record.isAvailable ? "In Stock" : "Out of Stock"}</TableCell>
                <TableCell>{canCreate && <Button size="small" disabled={saving} onClick={() => editRule(record.mealId._id)}>Date Rule</Button>}</TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        {(tab === 0 ? plan.rows : plan.scheduledMeals).length === 0 && <Typography sx={{ py: 3 }}>No meals for this date.</Typography>}
      </>}
    </DialogContent>
    <DialogActions><Button onClick={onClose} disabled={saving}>Close</Button></DialogActions>
  </Dialog>;
}
